import { NgModule } from '@angular/core';
import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import { RouterModule, Routes, PreloadAllModules } from '@angular/router';
import { ShellComponent } from './core/shell/shell.component';
import { AuthGuard } from './core/guards/auth.guard';
import { PlanGuard } from './core/guards/plan.guard';
import { SuperAdminGuard } from './core/guards/super-admin.guard';
import { PermissionGuard } from './core/guards/permission.guard';
import { AmcGuard } from './core/guards/amc.guard';

const routes: Routes = [
  // Auth routes (lazy loaded, no guard)
  {
    path: 'auth',
    loadChildren: () => import('./auth/auth.module').then(m => m.AuthModule)
  },

  // Super-admin panel (platform-level — separate shell, own routes)
  {
    path: 'super-admin',
    canActivate: [AuthGuard, SuperAdminGuard],
    loadChildren: () => import('./super-admin/super-admin.module').then(m => m.SuperAdminModule)
  },

  // Protected shell routes
  {
    path: '',
    component: ShellComponent,
    canActivate: [AuthGuard],
    canActivateChild: [AmcGuard],
    children: [
      {
        path: 'dashboard',
        loadChildren: () => import('./features/dashboard/dashboard.module').then(m => m.DashboardModule)
      },
      {
        path: 'members',
        canActivate: [PermissionGuard],
        data: { module: 'members' },
        loadChildren: () => import('./features/members/members.module').then(m => m.MembersModule)
      },
      {
        path: 'buyers',
        canActivate: [PermissionGuard],
        data: { module: 'buyers' },
        loadChildren: () => import('./features/buyers/buyers.module').then(m => m.BuyersModule)
      },
      {
        path: 'milk-collection',
        canActivate: [PermissionGuard],
        data: { module: 'milk_collection' },
        loadChildren: () => import('./features/milk-collection/milk-collection.module').then(m => m.MilkCollectionModule)
      },
      {
        path: 'payments',
        canActivate: [PermissionGuard],
        data: { module: 'payments' },
        loadChildren: () => import('./features/payments/payments.module').then(m => m.PaymentsModule)
      },
      {
        path: 'receipts',
        canActivate: [PermissionGuard],
        data: { module: 'receipts' },
        loadChildren: () => import('./features/receipts/receipts.module').then(m => m.ReceiptsModule)
      },
      {
        path: 'products',
        canActivate: [PermissionGuard],
        data: { module: 'products' },
        loadChildren: () => import('./features/products/products.module').then(m => m.ProductsModule)
      },
      {
        path: 'reports',
        canActivate: [PermissionGuard],
        data: { module: 'reports' },
        loadChildren: () => import('./features/reports/reports.module').then(m => m.ReportsModule)
      },
      {
        path: 'routes',
        canActivate: [PermissionGuard],
        data: { module: 'routes' },
        loadChildren: () => import('./features/routes/routes.module').then(m => m.RoutesModule)
      },
      {
        path: 'accounts',
        canActivate: [PermissionGuard],
        data: { module: 'accounts' },
        loadChildren: () => import('./features/accounts/accounts.module').then(m => m.AccountsModule)
      },
      {
        path: 'rate-list',
        canActivate: [PermissionGuard],
        data: { module: 'rate_list' },
        loadChildren: () => import('./features/rate-list/rate-list.module').then(m => m.RateListModule)
      },
      {
        path: 'gst-sales',
        canActivate: [PlanGuard, PermissionGuard],
        data: { feature: 'gst_billing', requiredPlan: 'PREMIUM', module: 'gst_sales' },
        loadChildren: () => import('./features/gst-sales/gst-sales.module').then(m => m.GstSalesModule)
      },
      {
        path: 'purchases',
        canActivate: [PermissionGuard],
        data: { module: 'purchases' },
        loadChildren: () => import('./features/purchases/purchases.module').then(m => m.PurchasesModule)
      },
      {
        path: 'gst-purchases',
        canActivate: [PlanGuard, PermissionGuard],
        data: { feature: 'gst_billing', requiredPlan: 'PREMIUM', module: 'gst_purchases' },
        loadChildren: () => import('./features/gst-purchases/gst-purchases.module').then(m => m.GstPurchasesModule)
      },
      {
        path: 'sales',
        canActivate: [PermissionGuard],
        data: { module: 'sales' },
        loadChildren: () => import('./features/sales/sales.module').then(m => m.SalesModule)
      },
      {
        path: 'milk-sale',
        canActivate: [PermissionGuard],
        data: { module: 'milk_sale' },
        loadChildren: () => import('./features/milk-sale/milk-sale.module').then(m => m.MilkSaleModule)
      },
      {
        path: 'daybook',
        canActivate: [PlanGuard, PermissionGuard],
        data: { feature: 'daybook', requiredPlan: 'BASIC', module: 'daybook' },
        loadChildren: () => import('./features/daybook/daybook.module').then(m => m.DaybookModule)
      },
      {
        path: 'ledger',
        canActivate: [PlanGuard, PermissionGuard],
        data: { feature: 'ledger', requiredPlan: 'BASIC', module: 'ledger' },
        loadChildren: () => import('./features/ledger/ledger.module').then(m => m.LedgerModule)
      },
      {
        path: 'milk-bill',
        canActivate: [PermissionGuard],
        data: { module: 'milk_bill' },
        loadChildren: () => import('./features/milk-bill/milk-bill.module').then(m => m.MilkBillModule)
      },
      {
        path: 'milk-transport-bill',
        canActivate: [PlanGuard, PermissionGuard],
        data: { feature: 'milk_dispatch', requiredPlan: 'PREMIUM', module: 'milk_transport_bill' },
        loadChildren: () => import('./features/milk-transport-bill/milk-transport-bill.module').then(m => m.MilkTransportBillModule)
      },
      {
        path: 'buyer-bill',
        canActivate: [PermissionGuard],
        data: { module: 'buyer_bill' },
        loadChildren: () => import('./features/buyer-bill/buyer-bill.module').then(m => m.BuyerBillModule)
      },
      {
        path: 'chilling-bill',
        canActivate: [PlanGuard, PermissionGuard],
        data: { feature: 'chilling_bill', requiredPlan: 'PREMIUM', module: 'chilling_bill' },
        loadChildren: () => import('./features/chilling-bill/chilling-bill.module').then(m => m.ChillingBillModule)
      },
      {
        path: 'head-load',
        canActivate: [PlanGuard, PermissionGuard],
        data: { feature: 'head_load', requiredPlan: 'PREMIUM', module: 'head_load' },
        loadChildren: () => import('./features/head-load/head-load.module').then(m => m.HeadLoadModule)
      },
      {
        path: 'dairy-users',
        canActivate: [PlanGuard, PermissionGuard],
        data: { feature: 'dairy_users', requiredPlan: 'ADVANCE', module: 'dairy_users' },
        loadChildren: () => import('./features/dairy-users/dairy-users.module').then(m => m.DairyUsersModule)
      },
      {
        path: 'sms-management',
        canActivate: [PermissionGuard],
        data: { module: 'sms_management' },
        loadChildren: () => import('./features/sms-management/sms-management.module').then(m => m.SmsManagementModule)
      },
      {
        path: 'store',
        loadChildren: () => import('./features/store/store.module').then(m => m.StoreModule)
      },
      {
        path: 'route-report',
        canActivate: [PlanGuard, PermissionGuard],
        data: { feature: 'route_report', requiredPlan: 'ADVANCE', module: 'route_report' },
        loadChildren: () => import('./features/route-report/route-report.module').then(m => m.RouteReportModule)
      },
      {
        path: 'route-milk-receipt',
        canActivate: [PlanGuard, PermissionGuard],
        data: { feature: 'route_report', requiredPlan: 'ADVANCE', module: 'route_milk_receipt' },
        loadChildren: () => import('./features/route-milk-receipt/route-milk-receipt.module').then(m => m.RouteMilkReceiptModule)
      },
      {
        path: 'trial-balance',
        canActivate: [PlanGuard, PermissionGuard],
        data: { feature: 'trial_balance', requiredPlan: 'ADVANCE', module: 'trial_balance' },
        loadChildren: () => import('./features/trial-balance/trial-balance.module').then(m => m.TrialBalanceModule)
      },
      {
        path: 'profit-loss',
        canActivate: [PlanGuard, PermissionGuard],
        data: { feature: 'trial_balance', requiredPlan: 'ADVANCE', module: 'profit_loss' },
        loadChildren: () => import('./features/profit-loss/profit-loss.module').then(m => m.ProfitLossModule)
      },
      {
        path: 'balance-sheet',
        canActivate: [PlanGuard, PermissionGuard],
        data: { feature: 'trial_balance', requiredPlan: 'ADVANCE', module: 'balance_sheet' },
        loadChildren: () => import('./features/balance-sheet/balance-sheet.module').then(m => m.BalanceSheetModule)
      },
      {
        path: 'milk-stock',
        canActivate: [PlanGuard, PermissionGuard],
        data: { feature: 'milk_stock', requiredPlan: 'PREMIUM', module: 'milk_stock' },
        loadChildren: () => import('./features/milk-stock/milk-stock.module').then(m => m.MilkStockModule)
      },
      {
        path: 'stock-inventory',
        canActivate: [PlanGuard, PermissionGuard],
        data: { feature: 'reports', requiredPlan: 'ADVANCE', module: 'stock_inventory' },
        loadChildren: () => import('./features/stock-inventory/stock-inventory.module').then(m => m.StockInventoryModule)
      },
      {
        path: 'milk-dispatch',
        canActivate: [PlanGuard, PermissionGuard],
        data: { feature: 'milk_dispatch', requiredPlan: 'PREMIUM', module: 'milk_dispatch' },
        loadChildren: () => import('./features/milk-dispatch/milk-dispatch.module').then(m => m.MilkDispatchModule)
      },
      {
        path: 'storage-locations',
        canActivate: [AuthGuard, PlanGuard],
        data: { feature: 'storage_locations', requiredPlan: 'PREMIUM' },
        loadChildren: () => import('./features/storage-locations/storage-locations.module').then(m => m.StorageLocationsModule)
      },
      {
        path: 'production',
        canActivate: [PlanGuard, PermissionGuard],
        data: { feature: 'production', requiredPlan: 'PREMIUM', module: 'production' },
        loadChildren: () => import('./features/production/production.module').then(m => m.ProductionModule)
      },
      {
        path: 'help',
        loadChildren: () => import('./features/help/help.module').then(m => m.HelpModule)
      },
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full'
      }
    ]
  },

  // Member Portal (separate shell, own auth — no AuthGuard)
  {
    path: 'member-portal',
    loadChildren: () => import('./member-portal/member-portal.module').then(m => m.MemberPortalModule)
  },

  // Fallback
  { path: '**', redirectTo: 'auth/login' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { useHash: true, preloadingStrategy: PreloadAllModules })],
  exports: [RouterModule],
  providers: [{ provide: LocationStrategy, useClass: HashLocationStrategy }]
})
export class AppRoutingModule { }
